import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Shield, Users, AlertTriangle, FileText, Handshake, Eye } from 'lucide-react';

const CGU = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-10 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-white text-3xl md:text-4xl font-black mb-2">
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-gray-400 text-sm md:text-base">
            Règles d'utilisation de la plateforme JJ Cars
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Avertissement principal */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-5 rounded-r-xl mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} style={{ color: '#F5A623' }} className="flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-black text-gray-900 mb-2">Information importante</h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong>JJ Cars est une plateforme de mise en relation</strong> entre acheteurs et vendeurs
                de véhicules. Nous ne sommes <strong>ni vendeur, ni acheteur, ni intermédiaire commercial</strong>.
                Les transactions, vérifications et accords se font <strong>directement et exclusivement</strong>
                entre l'acheteur et le vendeur.
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">

          {/* 1. Objet */}
          <Section icon={<FileText size={20} />} title="1. Objet de la plateforme">
            <p>
              JJ Cars est une marketplace en ligne dédiée à la mise en relation entre particuliers
              et professionnels souhaitant acheter, vendre ou louer des véhicules au Gabon.
            </p>
            <p>
              Le site agit uniquement comme un <strong>support technique de publication</strong>.
              Aucune transaction financière n'est traitée ni garantie par JJ Cars.
            </p>
          </Section>

          {/* 2. Rôle */}
          <Section icon={<Handshake size={20} />} title="2. Rôle de JJ Cars">
            <p>JJ Cars met à disposition :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Une plateforme de publication d'annonces</li>
              <li>Un système de messagerie entre utilisateurs</li>
              <li>Un système d'authentification et de vérification optionnel</li>
            </ul>
            <p className="font-semibold text-gray-900 mt-3">
              JJ Cars n'intervient PAS dans :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Les négociations entre acheteurs et vendeurs</li>
              <li>Les paiements (espèces, virements, chèques, etc.)</li>
              <li>La livraison ou le transfert des véhicules</li>
              <li>Le contrôle physique de l'état des véhicules</li>
            </ul>
          </Section>

          {/* 3. Obligations utilisateurs */}
          <Section icon={<Users size={20} />} title="3. Obligations des utilisateurs">
            <p className="font-semibold text-gray-900">L'acheteur s'engage à :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Vérifier <strong>par lui-même</strong> l'état du véhicule avant tout paiement</li>
              <li>Demander tous les documents (carte grise, contrôle technique, etc.)</li>
              <li>Rencontrer le vendeur en personne dans un lieu sûr</li>
              <li>Ne jamais verser d'argent sans avoir vu le véhicule</li>
            </ul>
            <p className="font-semibold text-gray-900 mt-3">Le vendeur s'engage à :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Fournir des informations exactes sur le véhicule</li>
              <li>Publier des photos réelles et récentes</li>
              <li>Disposer des documents légaux de propriété</li>
              <li>Respecter les lois en vigueur sur la vente de véhicules</li>
            </ul>
          </Section>

          {/* 4. Responsabilité */}
          <Section icon={<Shield size={20} />} title="4. Limitation de responsabilité">
            <p>
              JJ Cars <strong>décline toute responsabilité</strong> concernant :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>L'exactitude des informations publiées par les utilisateurs</li>
              <li>L'état réel des véhicules proposés</li>
              <li>Les litiges, escroqueries ou désaccords entre les parties</li>
              <li>Les pertes financières liées à une transaction</li>
              <li>Les défauts cachés ou vices constatés après achat</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Tout litige relatif à une transaction doit être réglé <strong>directement entre
              l'acheteur et le vendeur</strong>, le cas échéant par les voies légales appropriées.
            </p>
          </Section>

          {/* 5. Vérifications */}
          <Section icon={<Eye size={20} />} title="5. Vérifications recommandées">
            <p>Avant tout achat, nous recommandons fortement de :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Demander la carte grise originale</li>
              <li>Vérifier le numéro de châssis (VIN)</li>
              <li>Effectuer un essai routier</li>
              <li>Faire examiner le véhicule par un mécanicien</li>
              <li>Privilégier les rencontres en journée et dans un lieu public</li>
              <li>Éviter les paiements à distance ou par mandat</li>
            </ul>
          </Section>

          {/* 6. Données et compte */}
          <Section icon={<Users size={20} />} title="6. Création de compte et données">
            <p>
              En créant un compte, l'utilisateur s'engage à fournir des informations exactes
              et à respecter les présentes conditions. JJ Cars se réserve le droit de suspendre
              ou supprimer tout compte ne respectant pas ces règles.
            </p>
            <p>
              Les données personnelles sont utilisées uniquement pour le fonctionnement de la
              plateforme et ne sont pas revendues à des tiers.
            </p>
          </Section>

        </div>

        {/* Retour */}
        <div className="mt-10 text-center">
          <Link
            to="/inscription"
            style={{ background: '#F5A623' }}
            className="inline-block text-black px-6 py-3 rounded-xl font-black hover:opacity-90 transition"
          >
            Retour à l'inscription
          </Link>
        </div>

      </div>
    </div>
  );
};

const Section = ({ icon, title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
    <div className="flex items-center gap-3 mb-3">
      <div style={{ background: '#F5A62320' }} className="w-10 h-10 rounded-xl flex items-center justify-center">
        <span style={{ color: '#F5A623' }}>{icon}</span>
      </div>
      <h3 className="font-black text-gray-900 text-lg">{title}</h3>
    </div>
    <div className="text-gray-700 text-sm leading-relaxed space-y-2">
      {children}
    </div>
  </div>
);

export default CGU;
